import {
  AskForHelpEvent,
  AskForHelpStatus,
  buildEAFilter,
  buildEARequestQuery,
  buildEASearchQuery,
  CriteriaOperator,
  EARequestQueryParam,
  EpticaFields,
  EpticaRequestListParamDto,
  EpticaSearchParamDto,
  FilterOperator,
  RequestFields,
} from '@contact/upgrade';

/**
 * Build query to get ask for help status for given ids
 */
export function buildAskForHelpStatusQuery(ids: string[]): EpticaSearchParamDto {
  return buildEASearchQuery({
    filter: {
      operator: FilterOperator.AND,
      fields: [
        // match ids
        buildEAFilter(
          'multi-long',
          EpticaFields.ID,
          ids.map(id => Number(id)),
          CriteriaOperator.EQUALS
        ),
      ],
    },
    aggregations: {
      // by id
      [EpticaFields.ID]: {
        terms: { field: EpticaFields.ID, size: ids.length },
        aggregations: {
          // for nested event
          [EpticaFields.EVENT]: {
            nested: { path: EpticaFields.EVENT },
            aggregations: {
              // created ask for help
              [AskForHelpEvent.CREATED]: {
                filter: { term: { [EpticaFields.EVENT_TYPE]: AskForHelpEvent.CREATED } },
                aggregations: {
                  // open
                  [AskForHelpStatus.OPEN]: {
                    filter: { term: { [EpticaFields.ASK_FOR_HELP_STATUS]: AskForHelpStatus.OPEN } },
                    aggregations: {
                      [EpticaFields.TARGET_ACCOUNT_ID]: { terms: { field: EpticaFields.TARGET_ACCOUNT_ID } },
                    },
                  },
                  // answered
                  [AskForHelpStatus.ANSWERED]: {
                    filter: { term: { [EpticaFields.ASK_FOR_HELP_STATUS]: AskForHelpStatus.ANSWERED } },
                    aggregations: {
                      [EpticaFields.TARGET_ACCOUNT_ID]: { terms: { field: EpticaFields.TARGET_ACCOUNT_ID } },
                    },
                  },
                },
              },
              // created ask for help to external specialist
              [AskForHelpEvent.CREATED_TO_EXTERNAL_SPECIALIST]: {
                filter: { term: { [EpticaFields.EVENT_TYPE]: AskForHelpEvent.CREATED_TO_EXTERNAL_SPECIALIST } },
                aggregations: {
                  // open
                  [AskForHelpStatus.OPEN]: {
                    filter: { term: { [EpticaFields.ASK_FOR_HELP_STATUS]: AskForHelpStatus.OPEN } },
                    aggregations: {
                      [EpticaFields.TARGET_ACCOUNT_ID]: { terms: { field: EpticaFields.TARGET_ACCOUNT_ID } },
                    },
                  },
                  // answered
                  [AskForHelpStatus.ANSWERED]: {
                    filter: { term: { [EpticaFields.ASK_FOR_HELP_STATUS]: AskForHelpStatus.ANSWERED } },
                    aggregations: {
                      [EpticaFields.TARGET_ACCOUNT_ID]: { terms: { field: EpticaFields.TARGET_ACCOUNT_ID } },
                    },
                  },
                },
              },
            },
          },
        },
      },
    },
  });
}

export function buildMatchingRequestIdsQuery(
  ids: string[],
  query: Omit<EARequestQueryParam, 'namedFilter' | 'userFilter'>
): EpticaRequestListParamDto {
  return buildEARequestQuery({
    ...query,
    userFilter: {
      operator: FilterOperator.AND,
      fields: [
        // indexation status match fully_indexes
        buildEAFilter('string', EpticaFields.INDEXATION_STATUS, 'fully_indexed', CriteriaOperator.MATCH),
        // state not contains closed
        buildEAFilter('string', EpticaFields.STATE, 'closed', CriteriaOperator.NOT_CONTAINS),
      ],
      subFilters: [
        {
          operator: FilterOperator.OR,
          fields: [
            // status match ready_to_assign
            buildEAFilter('string', RequestFields.STATUS, 'ready_to_assign', CriteriaOperator.MATCH),
            // status match transfered
            buildEAFilter('string', RequestFields.STATUS, 'transfered', CriteriaOperator.MATCH),
          ],
        },
        {
          operator: FilterOperator.OR,
          fields: [
            // match ids
            buildEAFilter(
              'multi-long',
              EpticaFields.ID,
              ids.map(id => Number(id)),
              CriteriaOperator.EQUALS
            ),
          ],
        },
      ],
    },
  });
}

export function buildSearchLastSentMessageFrom(accoundId: number): EpticaRequestListParamDto {
  let currentTS = new Date().getTime();
  return buildEARequestQuery({
    userFilter: {
      operator: FilterOperator.AND,
      fields: [
        // type contains oe_mail_sent
        buildEAFilter('string', EpticaFields.TYPE, 'oe_mail_sent', CriteriaOperator.CONTAINS),
        // responsible id equals to account id
        buildEAFilter('int', EpticaFields.RESPONSIBLE_ACCOUNT_ID, accoundId, CriteriaOperator.EQUALS),
        // creation date range
        buildEAFilter('date-range', EpticaFields.CREATION_DATE, {
          from: currentTS - 60 * 1000,
          to: currentTS,
        }),
      ],
    },
    pageSize: 1,
    offset: 0,
    sort: {
      [EpticaFields.CREATION_DATE]: 'desc',
    },
  });
}
